/**
 * STALE-BUILD RELOAD (slice 56e).
 *
 * 🔴 THE DEFECT. An installed PWA on Android that is switched back to is RESUMED,
 * not reloaded: the JavaScript in memory is whatever build was loaded when the app
 * last cold-started — which can be a week old. Nothing ever told it a new build had
 * shipped: `sw.js` is byte-identical across deploys (so the browser's worker update
 * check finds nothing), and the worker's `activate` reloads open windows only when
 * it evicts a FOREIGN cache. Mahad, 21 Sep: the Alerts feed rendered every ignition
 * and tamper alert as `Entered ""` / `ZONES.PURPOSE.UNDEFINED` — reproduced exactly
 * by a pre-15-Sep build (before slice 13f, unknown feed types fell through to the
 * geofence row) against today's API. "Sometimes" = whenever the app was resumed
 * rather than cold-started.
 *
 * 🔴 THE CURE. When the app becomes VISIBLE again (resume), fetch `/` bypassing
 * every cache and compare the entry chunk it names with the one this page is
 * running. Different → reload once. Hashed filenames make the comparison exact
 * (the same identity `ci/check-deployed.mjs` asserts on).
 *
 * Only on resume — never on a timer while the user is looking at a screen. And
 * 🔴 NEVER OVER UNSAVED WORK (56e review): a user reading an SMS code for the
 * engine-cut PIN dialog, or half-way through drawing a zone, must not come back to
 * a reloaded page. While any modal dialog is open, any form field has been edited,
 * or a draw map is on screen, the reload is SKIPPED — the next resume retries.
 * Throttled, and every failure (offline, non-200, unparseable) means "do nothing":
 * this can only ever reload onto a build the origin is actually serving.
 */

const ENTRY_RE = /\/assets\/index-[A-Za-z0-9_-]+\.js/;
/** Do not re-check more often than this — a flurry of tab switches is one check. */
export const STALE_CHECK_MIN_INTERVAL_MS = 60_000;

/** The entry chunk path named in an HTML document, or null. */
export function entryChunkFromHtml(html: string): string | null {
  const m = ENTRY_RE.exec(html);
  return m ? m[0] : null;
}

/** The entry chunk THIS page is running, from its own module script tag. */
export function runningEntryChunk(doc: Document): string | null {
  for (const s of Array.from(doc.querySelectorAll<HTMLScriptElement>('script[type="module"][src]'))) {
    const m = ENTRY_RE.exec(s.getAttribute('src') ?? '');
    if (m) return m[0];
  }
  return null;
}

/** True only when both sides are known AND differ. Unknown on either side → false. */
export function isStale(running: string | null, served: string | null): boolean {
  return running !== null && served !== null && running !== served;
}

/**
 * Is there anything on this page a reload would destroy? Conservative by design —
 * a false "yes" only delays an update to the next resume; a false "no" loses work.
 */
export function hasUnsavedWork(doc: Document): boolean {
  if (doc.querySelector('[role="dialog"][aria-modal="true"], [role="alertdialog"], dialog[open]')) return true;
  // A zone / place draw map (GeofenceDrawMap, PlaceDrawMap mirror terra-draw's mode
  // onto the container). Being on that screen at all is enough — an in-progress
  // shape is not otherwise observable from the DOM.
  if (doc.querySelector('[data-draw-mode]')) return true;
  for (const el of Array.from(doc.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea'))) {
    if (el instanceof HTMLInputElement) {
      if (el.type === 'hidden') continue;
      if (el.type === 'checkbox' || el.type === 'radio') {
        if (el.checked !== el.defaultChecked) return true;
        continue;
      }
      // Range inputs (e.g. the replay scrubber) are view state, not user work.
      if (el.type === 'range') continue;
    }
    if (el.value !== el.defaultValue) return true;
  }
  return false;
}

export interface StaleBuildDeps {
  doc: Document;
  fetchHtml: () => Promise<string | null>;
  reload: () => void;
}

/** One check. Resolves true when it triggered a reload. Exported for tests. */
export async function checkForStaleBuild(deps: StaleBuildDeps): Promise<boolean> {
  const running = runningEntryChunk(deps.doc);
  if (running === null) return false; // dev server / unexpected shell — never guess
  let html: string | null = null;
  try {
    html = await deps.fetchHtml();
  } catch {
    return false;
  }
  if (html === null) return false;
  if (!isStale(running, entryChunkFromHtml(html))) return false;
  // Re-checked AFTER the fetch: the user may have opened a dialog meanwhile.
  if (hasUnsavedWork(deps.doc)) return false;
  deps.reload();
  return true;
}

async function fetchShell(): Promise<string | null> {
  // `no-store` so the HTTP cache cannot answer. The service worker treats this plain
  // GET as a static request — NETWORK-first, cache only when offline — so online it
  // is always the origin's current shell. ONE fixed URL (not a per-call cache-buster)
  // so the worker's static cache holds at most one entry for it, overwritten.
  const res = await fetch('/?build-check', { cache: 'no-store', credentials: 'same-origin' });
  if (!res.ok) return null;
  return res.text();
}

export interface ResumeEnv {
  doc: Document;
  isOnline: () => boolean;
  now: () => number;
  fetchHtml: () => Promise<string | null>;
  reload: () => void;
}

/** The resume handler, separated from the global wiring so its gates are testable:
 *  visible only, online only, one in flight, and at most one check per interval. */
export function createResumeHandler(env: ResumeEnv): () => Promise<boolean> {
  let lastCheck = Number.NEGATIVE_INFINITY;
  let inFlight = false;
  return async () => {
    // Offline, the worker would answer this GET from its cache — possibly an OLDER
    // shell than the one running. Only an online answer is evidence of what shipped.
    if (env.doc.visibilityState !== 'visible' || inFlight || !env.isOnline()) return false;
    const t = env.now();
    if (t - lastCheck < STALE_CHECK_MIN_INTERVAL_MS) return false;
    lastCheck = t;
    inFlight = true;
    try {
      return await checkForStaleBuild({ doc: env.doc, fetchHtml: env.fetchHtml, reload: env.reload });
    } finally {
      inFlight = false;
    }
  };
}

/** Install the resume listener. PROD only — a dev server has no hashed entry. */
export function installStaleBuildReload(): void {
  if (!import.meta.env.PROD || typeof document === 'undefined') return;
  const onResume = createResumeHandler({
    doc: document,
    isOnline: () => navigator.onLine,
    now: Date.now,
    fetchHtml: fetchShell,
    reload: () => window.location.reload(),
  });
  document.addEventListener('visibilitychange', () => {
    void onResume();
  });
}
